import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, Building2, CircleCheck as CheckCircle2, Circle as XCircle, Loader as Loader2, RefreshCw, QrCode, Power, Save, Send, Eye, EyeOff, TriangleAlert as AlertTriangle, Globe, Users, ShieldCheck } from 'lucide-react';

interface Unit {
  id: string;
  name: string;
}

interface Instance {
  id: string;
  unit_id: string;
  scope: string;
  seller_id: string | null;
  instance_name: string;
  phone_number: string | null;
  api_url: string;
  api_key: string;
  status: 'disconnected' | 'connecting' | 'connected' | 'failed';
  qr_code: string | null;
  last_connected_at: string | null;
}

interface Template {
  id?: string;
  unit_id: string;
  template_type: string;
  message_body: string;
}

interface SystemConfig {
  cron_enabled: boolean;
  functions_base_url: string | null;
  evolution_api_url: string | null;
  evolution_api_key: string | null;
  manual_send_only: boolean;
}

interface SellerInstance extends Instance {
  seller_name?: string;
}

const TEMPLATE_LABELS: Record<string, string> = {
  before_due: 'Antes do vencimento',
  on_due_day: 'No dia do vencimento',
  overdue_1d: 'Atraso de 1 dia',
  overdue_3d: 'Atraso de 3 dias',
  overdue_7d: 'Atraso de 7 dias',
  payment_confirmation: 'Confirmação de pagamento',
};

const TEMPLATE_ORDER = [
  'before_due',
  'on_due_day',
  'overdue_1d',
  'overdue_3d',
  'overdue_7d',
  'payment_confirmation',
];

async function callFn(path: string, body: Record<string, unknown>) {
  const { data: { session } } = await supabase.auth.getSession();
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Erro ${res.status}`);
  return data;
}

export default function WhatsAppSettings() {
  const { user } = useAuth();
  const [units, setUnits] = useState<Unit[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState<string>('');
  const [instance, setInstance] = useState<Instance | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [sysConfig, setSysConfig] = useState<SystemConfig | null>(null);
  const [sellerInstances, setSellerInstances] = useState<SellerInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [globalApiUrl, setGlobalApiUrl] = useState('');
  const [globalApiKey, setGlobalApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);

  const [showQrModal, setShowQrModal] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);

  const [showTestModal, setShowTestModal] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Teste de conexão do WhatsApp da unidade.');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);

  const [banner, setBanner] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      await loadUnits();
      await loadSysConfig();
      setLoading(false);
    })();
  }, [user]);

  useEffect(() => {
    if (selectedUnitId) {
      loadUnitData(selectedUnitId);
      loadSellerInstances(selectedUnitId);
    }
  }, [selectedUnitId]);

  const flash = (type: 'success' | 'error', msg: string) => {
    setBanner({ type, msg });
    setTimeout(() => setBanner(null), 4000);
  };

  const loadUnits = async () => {
    let query = supabase.from('units').select('id, name').eq('is_active', true).order('name');
    if (user?.role === 'manager' && user.unit_id) {
      query = query.eq('id', user.unit_id);
    }
    const { data } = await query;
    setUnits(data || []);
    if (data && data.length > 0 && !selectedUnitId) {
      setSelectedUnitId(user?.role === 'manager' && user.unit_id ? user.unit_id : data[0].id);
    }
  };

  const loadSysConfig = async () => {
    const { data } = await supabase
      .from('whatsapp_system_config')
      .select('cron_enabled, functions_base_url, evolution_api_url, evolution_api_key, manual_send_only')
      .maybeSingle();
    setSysConfig(data);
    setGlobalApiUrl(data?.evolution_api_url || '');
    setGlobalApiKey(data?.evolution_api_key || '');
  };

  const loadUnitData = async (unitId: string) => {
    const [instRes, templatesRes] = await Promise.all([
      supabase
        .from('whatsapp_instances')
        .select('*')
        .eq('unit_id', unitId)
        .eq('scope', 'unit')
        .is('seller_id', null)
        .maybeSingle(),
      supabase.from('whatsapp_message_templates').select('*').eq('unit_id', unitId),
    ]);
    setInstance(instRes.data);
    setTemplates(templatesRes.data || []);
  };

  const loadSellerInstances = async (unitId: string) => {
    const { data: instances } = await supabase
      .from('whatsapp_instances')
      .select('*')
      .eq('unit_id', unitId)
      .eq('scope', 'seller')
      .order('updated_at', { ascending: false });
    if (!instances || instances.length === 0) {
      setSellerInstances([]);
      return;
    }
    const ids = instances.map((i: any) => i.seller_id).filter(Boolean);
    const { data: sellers } = await supabase
      .from('users')
      .select('id, name')
      .in('id', ids);
    const nameById = new Map((sellers || []).map((s: any) => [s.id, s.name]));
    setSellerInstances(instances.map((i: any) => ({ ...i, seller_name: nameById.get(i.seller_id) || 'Vendedor' })));
  };

  const saveGlobalCredentials = async () => {
    if (user?.role !== 'admin') return;
    if (!globalApiUrl || !globalApiKey) {
      flash('error', 'Preencha URL e chave da API');
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('whatsapp_system_config')
        .update({
          evolution_api_url: globalApiUrl.trim(),
          evolution_api_key: globalApiKey.trim(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', 1);
      if (error) throw error;
      await loadSysConfig();
      flash('success', 'Credenciais globais salvas');
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const connectUnit = async () => {
    if (!selectedUnitId) return;
    setSaving(true);
    try {
      const res = await callFn('whatsapp-instance/create', {
        unit_id: selectedUnitId,
        scope: 'unit',
      });
      setInstance(res.instance);
      if (res.qrcode) {
        setQrCode(res.qrcode);
        setShowQrModal(true);
      } else {
        await openQr();
      }
      flash('success', 'Instância criada. Escaneie o QR Code.');
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro ao criar instância');
    } finally {
      setSaving(false);
    }
  };

  const refreshStatus = async () => {
    if (!selectedUnitId) return;
    try {
      await callFn('whatsapp-instance/status', { unit_id: selectedUnitId, scope: 'unit' });
      await loadUnitData(selectedUnitId);
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro ao consultar status');
    }
  };

  const openQr = async () => {
    if (!selectedUnitId) return;
    setQrLoading(true);
    setShowQrModal(true);
    try {
      const res = await callFn('whatsapp-instance/qrcode', { unit_id: selectedUnitId, scope: 'unit' });
      setQrCode(res.qrcode);
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro ao obter QR Code');
      setShowQrModal(false);
    } finally {
      setQrLoading(false);
    }
  };

  const disconnect = async () => {
    if (!selectedUnitId) return;
    if (!confirm('Desconectar o WhatsApp desta unidade?')) return;
    try {
      await callFn('whatsapp-instance/disconnect', { unit_id: selectedUnitId, scope: 'unit' });
      await loadUnitData(selectedUnitId);
      flash('success', 'Desconectado');
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro');
    }
  };

  const runTestSend = async () => {
    if (!selectedUnitId || !testPhone) return;
    setTestSending(true);
    setTestResult(null);
    try {
      await callFn('whatsapp-instance/test-send', {
        unit_id: selectedUnitId,
        scope: 'unit',
        phone: testPhone,
        message: testMessage,
      });
      setTestResult('Enviado com sucesso!');
    } catch (err) {
      setTestResult(err instanceof Error ? err.message : 'Falha no envio');
    } finally {
      setTestSending(false);
    }
  };

  const saveTemplate = async (t: Template) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('whatsapp_message_templates')
        .upsert(
          { ...t, updated_at: new Date().toISOString() },
          { onConflict: 'unit_id,template_type' },
        );
      if (error) throw error;
      flash('success', `Template "${TEMPLATE_LABELS[t.template_type]}" salvo`);
    } catch (err) {
      flash('error', err instanceof Error ? err.message : 'Erro');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (user?.role !== 'admin' && user?.role !== 'manager') {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          Acesso restrito.
        </div>
      </div>
    );
  }

  const statusBadge = (() => {
    if (!instance) return { label: 'Não configurado', color: 'bg-slate-100 text-slate-700', icon: XCircle };
    if (instance.status === 'connected') return { label: 'Conectado', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 };
    if (instance.status === 'connecting') return { label: 'Aguardando conexão', color: 'bg-amber-100 text-amber-800', icon: Loader2 };
    if (instance.status === 'failed') return { label: 'Falha', color: 'bg-red-100 text-red-800', icon: AlertTriangle };
    return { label: 'Desconectado', color: 'bg-slate-100 text-slate-700', icon: XCircle };
  })();

  const providerConfigured = !!(sysConfig?.evolution_api_url && sysConfig?.evolution_api_key);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-100 rounded-xl">
            <MessageSquare className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">WhatsApp</h1>
            <p className="text-sm text-slate-600">Configure o provedor global, o WhatsApp de cada unidade e os templates de mensagem.</p>
          </div>
        </div>
      </div>

      {banner && (
        <div className={`p-4 rounded-lg border ${
          banner.type === 'success'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {banner.msg}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <p className="font-medium mb-1">Envio manual pela Agenda</p>
          <p>As mensagens não são mais enviadas automaticamente. Os vendedores selecionam clientes e disparam pela Agenda, usando o próprio WhatsApp ou o da unidade.</p>
        </div>
      </div>

      {user?.role === 'admin' && (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-5 border-b border-slate-100 flex items-center gap-2">
            <Globe className="w-5 h-5 text-slate-700" />
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-slate-900">Provedor global (Evolution API)</h2>
              <p className="text-sm text-slate-500">Uma única conta Hostinger/Evolution atende todas as unidades e vendedores.</p>
            </div>
            {providerConfigured && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Configurado
              </span>
            )}
          </div>
          <div className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">URL da Evolution API</label>
                <input
                  type="text"
                  value={globalApiUrl}
                  onChange={(e) => setGlobalApiUrl(e.target.value)}
                  placeholder="https://sua-evolution-api.hostinger.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Chave da API</label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={globalApiKey}
                    onChange={(e) => setGlobalApiKey(e.target.value)}
                    placeholder="Chave global da Evolution API"
                    className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={saveGlobalCredentials}
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium transition-colors disabled:opacity-60"
            >
              <Save className="w-4 h-4" />
              Salvar credenciais globais
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <label className="block text-sm font-medium text-slate-700 mb-2">
          <Building2 className="inline w-4 h-4 mr-1" />
          Unidade
        </label>
        <select
          value={selectedUnitId}
          onChange={(e) => setSelectedUnitId(e.target.value)}
          disabled={user?.role === 'manager'}
          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-slate-100"
        >
          {units.map((u) => (
            <option key={u.id} value={u.id}>{u.name}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">WhatsApp da unidade</h2>
            <p className="text-sm text-slate-500">Número institucional usado quando o vendedor não tem WhatsApp pessoal conectado.</p>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusBadge.color}`}>
            <statusBadge.icon className={`w-4 h-4 ${instance?.status === 'connecting' ? 'animate-spin' : ''}`} />
            {statusBadge.label}
          </span>
        </div>

        <div className="p-5 space-y-4">
          {!providerConfigured && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
              Configure o provedor global antes de conectar unidades.
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={connectUnit}
              disabled={saving || !providerConfigured}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-60"
            >
              <QrCode className="w-4 h-4" />
              {instance ? 'Reconectar / Nova instância' : 'Conectar WhatsApp'}
            </button>
            <button
              onClick={openQr}
              disabled={!instance}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg font-medium transition-colors disabled:opacity-40"
            >
              <QrCode className="w-4 h-4" />
              QR Code
            </button>
            <button
              onClick={refreshStatus}
              disabled={!instance}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-800 rounded-lg font-medium transition-colors disabled:opacity-40"
            >
              <RefreshCw className="w-4 h-4" />
              Atualizar status
            </button>
            <button
              onClick={() => setShowTestModal(true)}
              disabled={instance?.status !== 'connected'}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-40"
            >
              <Send className="w-4 h-4" />
              Testar envio
            </button>
            {instance && (
              <button
                onClick={disconnect}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-red-300 text-red-700 hover:bg-red-50 rounded-lg font-medium transition-colors ml-auto"
              >
                <Power className="w-4 h-4" />
                Desconectar
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100 flex items-center gap-2">
          <Users className="w-5 h-5 text-slate-700" />
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Vendedores conectados</h2>
            <p className="text-sm text-slate-500">Cada vendedor pode sincronizar o próprio WhatsApp na página "Meu WhatsApp".</p>
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {sellerInstances.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              Nenhum vendedor sincronizou o próprio WhatsApp ainda.
            </div>
          ) : (
            sellerInstances.map((si) => {
              const badge = si.status === 'connected'
                ? { label: 'Conectado', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 }
                : si.status === 'connecting'
                ? { label: 'Aguardando', color: 'bg-amber-100 text-amber-800', icon: Loader2 }
                : si.status === 'failed'
                ? { label: 'Falha', color: 'bg-red-100 text-red-800', icon: AlertTriangle }
                : { label: 'Desconectado', color: 'bg-slate-100 text-slate-700', icon: XCircle };
              const Icon = badge.icon;
              return (
                <div key={si.id} className="p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                    <span className="text-slate-700 font-medium text-sm">
                      {(si.seller_name || 'V').slice(0, 1).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{si.seller_name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {si.last_connected_at
                        ? `Última conexão: ${new Date(si.last_connected_at).toLocaleString('pt-BR')}`
                        : 'Nunca conectou'}
                    </p>
                  </div>
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                    <Icon className={`w-3.5 h-3.5 ${si.status === 'connecting' ? 'animate-spin' : ''}`} />
                    {badge.label}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-5 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">Templates de mensagem</h2>
          <p className="text-sm text-slate-500">
            Variáveis: <code className="text-emerald-700">{'{{cliente}}'}</code>, <code className="text-emerald-700">{'{{valor}}'}</code>, <code className="text-emerald-700">{'{{data}}'}</code>, <code className="text-emerald-700">{'{{parcela}}'}</code>
          </p>
        </div>
        <div className="divide-y divide-slate-100">
          {TEMPLATE_ORDER.map((type) => {
            const t = templates.find((tp) => tp.template_type === type) || {
              unit_id: selectedUnitId,
              template_type: type,
              message_body: '',
            };
            return (
              <div key={type} className="p-5 space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-slate-900">{TEMPLATE_LABELS[type]}</h3>
                </div>
                <textarea
                  value={t.message_body}
                  onChange={(e) => {
                    const updated = { ...t, message_body: e.target.value };
                    const others = templates.filter((tp) => tp.template_type !== type);
                    setTemplates([...others, updated]);
                  }}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
                />
                <button
                  onClick={() => saveTemplate({ ...t, unit_id: selectedUnitId })}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white text-sm rounded-md transition-colors disabled:opacity-60"
                >
                  <Save className="w-3.5 h-3.5" />
                  Salvar template
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {showQrModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Escaneie no WhatsApp</h3>
              <button onClick={() => setShowQrModal(false)} className="text-slate-500 hover:text-slate-800">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600">
              Abra o WhatsApp da unidade &gt; Configurações &gt; Aparelhos conectados &gt; Conectar um aparelho.
            </p>
            <div className="flex items-center justify-center min-h-[280px]">
              {qrLoading ? (
                <Loader2 className="w-10 h-10 text-emerald-600 animate-spin" />
              ) : qrCode ? (
                <img
                  src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`}
                  alt="QR Code"
                  className="w-64 h-64"
                />
              ) : (
                <p className="text-sm text-slate-500">QR Code indisponível</p>
              )}
            </div>
            <button
              onClick={openQr}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg font-medium transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Atualizar QR
            </button>
            <button
              onClick={() => { setShowQrModal(false); refreshStatus(); }}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
            >
              <CheckCircle2 className="w-4 h-4" />
              Já escaneei
            </button>
          </div>
        </div>
      )}

      {showTestModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Testar envio</h3>
              <button onClick={() => { setShowTestModal(false); setTestResult(null); }} className="text-slate-500 hover:text-slate-800">
                <XCircle className="w-5 h-5" />
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Telefone (com DDD)</label>
              <input
                type="text"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="11987654321"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mensagem</label>
              <textarea
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-sm"
              />
            </div>
            {testResult && (
              <div className={`p-3 rounded-lg text-sm ${testResult === 'Enviado com sucesso!' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                {testResult}
              </div>
            )}
            <button
              onClick={runTestSend}
              disabled={testSending || !testPhone}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-60"
            >
              {testSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Enviar teste
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
